import React from 'react'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'

import SliderCheckbox from './SliderCheckbox'
import { SettingsContext } from '../context/settings'

import { Filter } from '../types'
import Button from './Button'
import Dropdown from './Dropdown'

type DealsFiltersModalProps = {
  children?: React.ReactNode
  open: boolean
  isCompletedLayout: boolean
  onSearch: (formData: FiltersFormInputs) => void
}

export type FiltersFormInputs = {
  seller?: Array<string>
  energyMarket?: Array<string>
  scale?: Array<string>
  assetScope?: Array<string>
  entityType?: Array<string>
  assetType?: Array<string>
  sellFinancialAdvisor?: Array<string>
  sellFinancialAdvisorContact?: Array<string>
  sellLegalAdvisor?: Array<string>
  buyFinancialAdvisor?: Array<string>
  buyFinancialAdvisorContact?: Array<string>
  buyLegalAdvisor?: Array<string>
}

type FormError = {
  form?: string
}

function filterToOptions({ options }: Pick<Filter, 'options'>) {
  return options
    .filter((e) => e.name)
    .map((e) => ({ label: e.name, value: e.slug }))
}

function DealsFiltersContent(props: DealsFiltersModalProps) {
  const { isCompletedLayout, onSearch, open } = props
  const formRef = React.useRef<HTMLFormElement>(null)

  const settings = React.useContext(SettingsContext)

  const filters = settings?.filters?.deals
  const { control, clearErrors, setError, reset, handleSubmit } = useForm<
    FiltersFormInputs & FormError
  >({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  // @ts-ignore
  async function onSubmit(...args) {
    clearErrors('form')
    return handleSubmit(async (data) => {
      try {
        clearErrors('form')

        onSearch(data)
      } catch (error: any) {
        setError('form', {
          type: 'manual',
          message: error?.message,
        })
      }
    })(...args)
  }

  function onAnimationStart() {
    if (formRef?.current) {
      formRef.current.className = 'overflow-hidden'
      formRef.current.style.visibility = 'visible'
    }
  }

  function onAnimationEnd() {
    if (formRef?.current) {
      formRef.current.className = ''
      if (formRef.current.style.opacity === '0') {
        formRef.current.style.visibility = 'hidden'
      }
    }
  }

  return (
    <motion.form
      key="deals-filters-content"
      onSubmit={onSubmit}
      initial="collapsed"
      animate={open ? 'open' : 'collapsed'}
      variants={{
        open: { opacity: 1, height: 'auto' },
        collapsed: { opacity: 0, height: 0 },
      }}
      transition={{ duration: 0.8 }}
      ref={formRef}
      onAnimationStart={onAnimationStart}
      onAnimationComplete={onAnimationEnd}
    >
      <div className="filters-grid">
        <span className="filter-heading">Asset</span>
        {filters?.sizes?.filters?.mw?.label && (
          <SliderCheckbox
            id="mw-gw"
            name="mw-gw"
            label={filters?.sizes?.filters?.mw?.label || ''}
            suffix={filters?.sizes?.filters?.mw?.label}
            min={filters?.sizes?.filters?.mw?.low}
            max={filters?.sizes?.filters?.mw?.high}
            control={control}
          />
        )}

        {filters?.sizes?.filters?.mwh?.label && (
          <SliderCheckbox
            id="mwh-gwh"
            name="mwh-gwh"
            label={filters?.sizes?.filters?.mwh?.label || ''}
            suffix={filters?.sizes?.filters?.mwh?.label}
            min={filters?.sizes?.filters?.mwh?.low}
            max={filters?.sizes?.filters?.mwh?.high}
            control={control}
          />
        )}

        {filters?.sizes?.filters?.rces?.label && (
          <SliderCheckbox
            id="rces"
            name="rces"
            label={filters?.sizes?.filters?.rces?.label || ''}
            suffix={filters?.sizes?.filters?.rces?.label}
            min={filters?.sizes?.filters?.rces?.low}
            max={filters?.sizes?.filters?.rces?.high}
            control={control}
          />
        )}

        {filters?.general.label && (
          <span className="filter-heading">{filters?.general.label}</span>
        )}
        {/* @ts-ignore */}
        <Dropdown
          id="seller-dropdown"
          label={filters?.general.filters.sellers.label}
          options={filterToOptions({
            options: filters?.general?.filters?.sellers?.options || [],
          })}
          name="seller"
          control={control}
        />

        {/* @ts-ignore */}

        <Dropdown
          id="energy-market-dropdown"
          label={filters?.general.filters.markets.label}
          options={filterToOptions({
            options: filters?.general.filters.markets?.options || [],
          })}
          name="energyMarket"
          control={control}
        />
        {/* @ts-ignore */}

        <Dropdown
          id="scale-dropdown"
          label={filters?.general.filters.scales.label}
          options={filterToOptions({
            options: filters?.general.filters.scales?.options || [],
          })}
          name="scale"
          control={control}
        />
        {/* @ts-ignore */}

        <Dropdown
          id="asset-scope-dropdown"
          label={filters?.assets.filters.scopes.label}
          options={filterToOptions({
            options: filters?.assets.filters.scopes?.options || [],
          })}
          name="assetScope"
          control={control}
        />
        {/* @ts-ignore */}

        <Dropdown
          id="entity-type-dropdown"
          label={filters?.assets.filters.entities.label}
          options={filterToOptions({
            options: filters?.assets.filters.entities?.options || [],
          })}
          name="entityType"
          control={control}
        />
        {/* @ts-ignore */}

        <Dropdown
          id="asset-type-dropdown"
          label={filters?.assets.filters.types.label}
          options={filterToOptions({
            options: filters?.assets.filters.types?.options || [],
          })}
          name="assetType"
          control={control}
        />
        {filters?.sellside.label && (
          <span className="filter-heading">{filters?.sellside.label}</span>
        )}
        {/* @ts-ignore */}

        <Dropdown
          id="sell-financial-advisor-dropdown"
          label={filters?.sellside.filters.financial_advisors.label}
          options={filterToOptions({
            options:
              filters?.sellside.filters.financial_advisors?.options || [],
          })}
          name="sellFinancialAdvisor"
          control={control}
        />
        {/* @ts-ignore */}

        <Dropdown
          id="sell-financial-advisor-contact-dropdown"
          label={filters?.sellside.filters.financial_contacts.label}
          options={filterToOptions({
            options:
              filters?.sellside.filters.financial_contacts?.options || [],
          })}
          name="sellFinancialAdvisorContact"
          control={control}
        />
        {/* @ts-ignore */}

        <Dropdown
          id="sell-legal-advisor-dropdown"
          label={filters?.sellside.filters.legal_advisors.label}
          options={filterToOptions({
            options: filters?.sellside.filters.legal_advisors?.options || [],
          })}
          name="sellLegalAdvisor"
          control={control}
        />
        {isCompletedLayout && (
          <>
            {filters?.buyside.label && (
              <span className="filter-heading">{filters?.buyside.label}</span>
            )}
            {/* @ts-ignore */}

            <Dropdown
              id="buy-financial-advisor-dropdown"
              label={filters?.buyside.filters.financial_advisors.label}
              options={filterToOptions({
                options:
                  filters?.buyside.filters.financial_advisors?.options || [],
              })}
              name="buyFinancialAdvisor"
              control={control}
            />
            {/* @ts-ignore */}

            <Dropdown
              id="buy-financial-advisor-contact-dropdown"
              label={filters?.buyside.filters.financial_contacts.label}
              options={filterToOptions({
                options:
                  filters?.buyside.filters.financial_contacts?.options || [],
              })}
              name="buyFinancialAdvisorContact"
              control={control}
            />
            {/* @ts-ignore */}

            <Dropdown
              id="buy-legal-advisor-dropdown"
              label={filters?.buyside.filters.legal_advisors.label}
              options={filterToOptions({
                options: filters?.buyside.filters.legal_advisors?.options || [],
              })}
              name="buyLegalAdvisor"
              control={control}
            />
          </>
        )}

        <div className="col-span-full flex items-center gap-8">
          <Button>Show results</Button>
          <button type="button" className="btn-clear" onClick={() => reset()}>
            Clear all
          </button>
        </div>
        <style jsx>{`
          .filters-grid {
            @apply grid grid-cols-1 py-12 md:grid-cols-3 md:gap-x-8 lg:gap-x-24;
          }
          .btn-clear {
            @apply font-inter text-[14px] font-semibold leading-[16.94px] text-black underline;
          }
          .btn-black {
            @apply flex h-[47px] w-[149px] items-center justify-center rounded-[5px] bg-black;
            @apply font-inter text-[15px] font-semibold leading-[18.15px] text-white;
          }
          .filter-heading {
            @apply col-span-full mb-[16px] font-ibm text-[21px] font-semibold uppercase leading-[27.3px] text-black;
          }
        `}</style>
      </div>
    </motion.form>
  )
}

export default DealsFiltersContent
